import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import {
  CreateDeviceMut,
  CreateExternalConnectionMut,
  FetchDeviceQuery,
  FetchSubDevicesQuery,
  UpdateSecondaryDeviceMut,
} from '@/lib/api/devices/devices';
import { UpdateSecondaryDeviceRequest } from '@/lib/api/types.gen';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';

export const RefreshCamerasButton = ({ deviceId }: { deviceId: string }) => {
  const deviceQuery = useQuery(FetchDeviceQuery({ deviceId }));
  const device = deviceQuery.data;
  const subDevicesQuery = useQuery(FetchSubDevicesQuery({ deviceId }));
  const subDevices = subDevicesQuery.data;
  const queryClient = useQueryClient();

  const createDeviceMut = useMutation({
    ...CreateDeviceMut(),
    onError(error) {
      toast({
        title: 'Failed to refresh cameras',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  const createExternalConnectionMut = useMutation({
    ...CreateExternalConnectionMut(),
    onError(error) {
      toast({
        title: 'Failed to refresh cameras',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  const updateSecondaryDeviceMut = useMutation({
    ...UpdateSecondaryDeviceMut(deviceId),
    onError(error) {
      toast({
        title: 'Failed to start monitoring',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  const isRefreshing =
    createDeviceMut.isPending ||
    createExternalConnectionMut.isPending ||
    updateSecondaryDeviceMut.isPending;

  const handleRefreshCameras = async () => {
    if (device == null) return;

    const isVms = device.externalConnectionLimitedDto != null;
    

    // Serial number isnt actually used on BE
    const serialNumber = '';

    const common = {
      id: deviceId,
      name: device.name,
      serialNumber,
      description: device.description,
      customerId: device.customerId,
      projectId: device.projectId,
      address: device.address ?? {},
    };

    // Re-connect to device which will fetch any new devices
    // VMS devices use create-device endpoint, integrations use connect endpoint
    const res = isVms
      ? await createExternalConnectionMut.mutateAsync({
          ...common,
          externalConnectionId: device.externalConnectionLimitedDto?.id,
          deviceCreationChoices: device.deviceCreationChoices,
        })
      : await createDeviceMut.mutateAsync({
          ...common,
          fields: device.integrationKeyValue,
          integrationId: device.integration.id,
        });

    const existing = new Map(subDevices?.map((x) => [x.id, x]));
    const resDevices = new Map(res.subDevices?.map((x) => [x.id, x]));
    const added = [...resDevices.values()].filter((x) => !existing.has(x.id));
    // const removed = [...existing.values()].filter((x) => !resDevices.has(x.id));

    const updatedDevices: UpdateSecondaryDeviceRequest[] = [
      ...(subDevices ?? []),
      ...Object.values(added).map((device): UpdateSecondaryDeviceRequest => {
        return {
          id: device.id,
          monitored: false, // Set all new devices to false
          capabilities: device.capabilities,
          quillNotes: '',
        };
      }),
    ];

    // Re-set the monitoring state, otherwise all cameras are on by default
    await updateSecondaryDeviceMut.mutateAsync({
      primaryDeviceId: deviceId,
      devices: updatedDevices,
    });

    const addedStr =
      added.length > 0 ? `Added ${added.length} camera${added.length === 1 ? '' : 's'}` : '';

    // TODO: How to handle "removed" devices?
    // const removedStr =
    //   removed.length > 0
    //     ? `Removed ${removed.length} camera${removed.length === 1 ? '' : 's'}`
    //     : '';

    toast({
      title: 'Cameras refreshed',
      description: `${addedStr}`,
    });

    queryClient.invalidateQueries({
      queryKey: FetchSubDevicesQuery({ deviceId }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: FetchDeviceQuery({ deviceId }).queryKey,
    });
  };

  return (
    <Button variant="outline" onClick={handleRefreshCameras} disabled={isRefreshing}>
      <RefreshCw className="mr-2 h-4 w-4" />
      {isRefreshing ? 'Refreshing...' : 'Refresh Cameras'}
    </Button>
  );
};
